/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSearchbarWithApiV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../../redux/actions";
import CloseIcon from "@mui/icons-material/Close";

const CustomSearchbarWithApiV2 = ({
  apiAction,
  params = {},
  skipApiCall,
  searchKey,
  getDataOnSearch,
  triggerApi,
  onSearchQueryChange,
  size,
  type,
  variant,
  placeholder,
  valueError,
  isMultiline,
  minRows,
  maxRows,
  disabled,
  txtFldStyle = {},
  iconColor = "black",
  setSearchFieldValue,
  skipInitialSearch = true, // NEW: Skip initial API call on mount
}) => {
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const prevParamsRef = useRef(params);
  const didMountRef = useRef(false);
  const searchTextRef = useRef("");
  const [searchText, setSearchText] = useState("");
  const [prevSearch, setPrevSearch] = useState("");
  const [loading, setLoading] = useState(false);
  console.log(params, "paramsparamsparams");

  useEffect(() => {
    setSearchText("");
  }, []);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const handleSearch = async (query) => {
    console.log("---handleSearch---", query);
    if (onSearchQueryChange) {
      onSearchQueryChange(query, prevSearch);
    }

    if (skipApiCall) {
      getDataOnSearch([], { error: false, data: [] });
      return;
    }

    setLoading(true);

    setPrevSearch(query);

    try {
      console.log(skipApiCall);
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiAction,
            params: {
              ...params,
              ...(query !== "" && { [searchKey]: query }),
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      console.log("search response", response);

      if (response?.error) {
        getDataOnSearch([], response);
      } else {
        getDataOnSearch(response.data, response);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      getDataOnSearch([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const delayDebounce = setTimeout(() => {
      handleSearch(searchText);
    }, 500);

    return () => clearTimeout(delayDebounce); // cleanup on next effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    const paramsChanged =
      JSON.stringify(prevParamsRef.current) !== JSON.stringify(params);

    if (isFirstRun.current) {
      if (!skipInitialSearch) {
        handleSearch(searchTextRef.current);
      }
      isFirstRun.current = false;
    } else if (paramsChanged) {
      handleSearch(searchTextRef.current);
    }

    prevParamsRef.current = params; // update reference without rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, skipInitialSearch]);

  useEffect(() => {
    if (!isFirstRun.current) {
      handleSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerApi]);

  const clearSearch = () => {
    if (onSearchQueryChange) {
      onSearchQueryChange("", searchText);
    }
    setSearchText("");
    handleSearch("");
  };

  const handleSearchValue = (e) => {
    setSearchText(e.target.value);
  };

  return (
    <TextField
      fullWidth
      size={size}
      type={type}
      variant={variant}
      placeholder={placeholder}
      value={searchText}
      error={Boolean(valueError)}
      helperText={valueError}
      multiline={isMultiline}
      minRows={minRows}
      maxRows={maxRows}
      disabled={disabled}
      onChange={handleSearchValue}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSearch(searchText);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: iconColor }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              width: 32, // 🔒 FIXED WIDTH
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: iconColor }} />
            ) : (
              searchText && (
                <IconButton onClick={clearSearch} size="small">
                  <CloseIcon sx={{ color: iconColor }} />
                </IconButton>
              )
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: "28px",
          "& fieldset": { borderColor: "#D0D0CE", borderRadius: "0px" }, // Default
          "&:hover fieldset": { borderColor: "#D0D0CE" }, // Hover
          "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
          "&.Mui-disabled fieldset": { borderColor: "#D0D0CE" }, // Disabled
          "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error
        },
        "& .MuiOutlinedInput-root:hover .MuiInputBase-input::placeholder": {
          color: "#848587",
        },

        "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input::placeholder":
        {
          color: "#232323",
          opacity: 1,
        },

        "& .MuiInputLabel-root": {
          color: "#000",
          fontSize: "0.9rem",
          "&.Mui-error": { color: "#e53935" },
          "&.Mui-disabled": { color: "#bdbdbd" },
        },

        "& .MuiInputBase-input::placeholder": {
          p: 0,
          m: 0,
          color: "#999",
          opacity: 1,
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#e53935",
          fontSize: "0.8rem",
          marginLeft: 0,
          marginTop: "4px",
        },
        ...txtFldStyle,
      }}
    />
  );
};

CustomSearchbarWithApiV2.propTypes = {
  apiAction: PropTypes.object,
  params: PropTypes.object,
  skipApiCall: PropTypes.bool,
  searchKey: PropTypes.string,
  getDataOnSearch: PropTypes.func,
  triggerApi: PropTypes.any,
  onSearchQueryChange: PropTypes.func,
  size: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  valueError: PropTypes.string,
  isMultiline: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  disabled: PropTypes.bool,
  txtFldStyle: PropTypes.object,
  iconColor: PropTypes.string,
  setSearchFieldValue: PropTypes.func,
  skipInitialSearch: PropTypes.bool,
};

export default CustomSearchbarWithApiV2;
